/**
 * Pure price formatter for the pro profile services catalogue (ticket #5598).
 *
 * A price is two axes: `priceType` (how the amount reads: fixed / from / range) and
 * `priceUnit` (what it is charged per: job / sqm / item / hour). This util composes the
 * two — the type produces the base amount string, the unit appends a `/ suffix` (except
 * `job`, which is a flat price with no suffix). The amount runs through the country
 * currency formatter (Greek `.`/`,` grouping comes for free); the `από` prefix and unit
 * suffixes come from i18n. The `+ VAT` subline is the caller's concern (see
 * `ServicePrice.tsx`) — this util only renders the amount.
 *
 * Returns `undefined` for any price the row cannot render (unknown type/unit, missing
 * amount, range without an upper bound) so the caller can skip it.
 */

import { TFunction } from 'i18next';
import { Formatter } from 'Douleutaras/hooks/localization';
import { GENERAL } from 'Douleutaras/constants/general';

import { PriceType, PriceUnit, ServicePrice } from './types';

export interface PriceFormatDeps {
  formatter: Pick<Formatter, 'number'>;
  t: TFunction;
}

const isRenderableAmount = (value: number | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/** Every price type this util can render — exhaustive over `PriceType`, so a new one won't slip by. */
const RENDERABLE_PRICE_TYPES: Record<PriceType, true> = { fixed: true, from: true, range: true };

/** Every unit this util can render — exhaustive over `PriceUnit`, so a new one won't slip by. */
const RENDERABLE_PRICE_UNITS: Record<PriceUnit, true> = { job: true, sqm: true, item: true, hour: true };

/**
 * Whether a price renders at all, i.e. whether the row shows an amount rather than label-only.
 * {@link formatPrice} guards on this, so tracking (which reports the displayed amount) and the
 * UI cannot drift apart.
 */
export const isDisplayablePrice = (price?: ServicePrice): price is ServicePrice => {
  if (!price || !isRenderableAmount(price.amount) || !RENDERABLE_PRICE_TYPES[price.priceType]) {
    return false;
  }
  if (price.priceType === 'range' && !isRenderableAmount(price.amountMax)) {
    return false;
  }

  // `job` is flat and needs no suffix; every other unit needs one to render.
  return RENDERABLE_PRICE_UNITS[price.priceUnit] === true;
};

/** The base amount string for a price type, or `undefined` when it cannot be rendered. */
const formatAmount = (price: ServicePrice, { formatter, t }: PriceFormatDeps): string | undefined => {
  // Symbol-first (`€250`), matching the design. `formatter.currency` follows the locale, which for
  // Greek places the sign after the number (`250€`); all supported countries are Eurozone, so we
  // prefix the euro sign to the grouped number ourselves for a consistent, deterministic placement.
  const amount = `${GENERAL.CURRENCY.EUR}${formatter.number(price.amount)}`;

  switch (price.priceType) {
    case 'fixed':
      return amount;

    case 'from':
      return `${t('T.PROS-PROFILE.SERVICES.PRICE-FROM')} ${amount}`;

    case 'range':
      if (!isRenderableAmount(price.amountMax)) {
        return undefined;
      }

      // Euro sign on the lower bound only, upper bound as a plain grouped number: `€800-3.000`.
      return `${amount}-${formatter.number(price.amountMax)}`;

    default:
      // Unknown price type from BE → skip the row.
      return undefined;
  }
};

/**
 * The `/ suffix` copy for a per-unit price, or `undefined` for a unit we cannot render.
 * `job` is a flat price and never reaches here — see `formatPrice`.
 */
const formatUnitSuffix = (priceUnit: PriceUnit, t: TFunction): string | undefined => {
  switch (priceUnit) {
    case 'sqm':
      return t('T.PROS-PROFILE.SERVICES.UNIT-AREA');

    case 'item':
      return t('T.PROS-PROFILE.SERVICES.UNIT-ITEM');

    case 'hour':
      return t('T.PROS-PROFILE.SERVICES.UNIT-HOUR');

    default:
      // Unknown unit from BE → skip the row.
      return undefined;
  }
};

/**
 * Formats a service price for display, or `undefined` when it cannot be rendered.
 *
 * | type × unit    | Rendered (Greek)  |
 * |----------------|-------------------|
 * | fixed  · job   | `€150`            |
 * | from   · job   | `από €150`        |
 * | range  · job   | `€800-3.000`      |
 * | fixed  · sqm   | `€35 / τ.μ.`      |
 * | fixed  · item  | `€40 / τεμ.`      |
 * | fixed  · hour  | `€30 / ώρα`       |
 */
export const formatPrice = (
  price: ServicePrice | undefined,
  deps: PriceFormatDeps
): string | undefined => {
  if (!isDisplayablePrice(price)) {
    return undefined;
  }

  const base = formatAmount(price, deps);
  if (!base) {
    return undefined;
  }

  // `job` is a flat price — no per-unit suffix.
  if (price.priceUnit === 'job') {
    return base;
  }

  const unitSuffix = formatUnitSuffix(price.priceUnit, deps.t);
  if (!unitSuffix) {
    return undefined;
  }

  return `${base} / ${unitSuffix}`;
};
